import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { Icon } from '@ibk/shared-ui';
import styles from './StatusWizard.module.css';
const getCurrentStepIndex = (currentStatus) => {
    switch (currentStatus) {
        case 'CREATED':
            return 0;
        case 'IN_TRANSIT':
            return 1;
        case 'DELIVERED':
        case 'CANCELLED':
            return 2;
        default: {
            const exhaustiveStatus = currentStatus;
            return exhaustiveStatus;
        }
    }
};
const getWizardStepStatus = (isFinalCancelledStep, currentStatus, stepIndex, currentStepIndex) => {
    if (isFinalCancelledStep) {
        return 'cancelled';
    }
    if (currentStatus === 'DELIVERED' || stepIndex < currentStepIndex) {
        return 'completed';
    }
    if (stepIndex === currentStepIndex) {
        return 'current';
    }
    return 'pending';
};
const renderStepIndicatorContent = (stepStatus, stepIndex) => {
    switch (stepStatus) {
        case 'cancelled':
            return (_jsx(Icon, { name: 'close', variant: 'mask', tintColor: 'var(--text-neutral-white)', className: styles.cancelledIcon, "data-testid": `iconStatusWizardStepClose${stepIndex}` }));
        case 'completed':
            return _jsx(Icon, { name: 'checkMark', "data-testid": `iconStatusWizardStepCheckMark${stepIndex}` });
        case 'current':
        case 'pending':
            return (_jsx("span", { className: styles.stepNumber, "data-testid": `textViewStatusWizardStepNumber${stepIndex}`, children: stepIndex + 1 }));
        default: {
            const exhaustiveStepStatus = stepStatus;
            return exhaustiveStepStatus;
        }
    }
};
export function StatusWizard({ currentStatus }) {
    const { t } = useTranslation();
    const currentStepIndex = getCurrentStepIndex(currentStatus);
    const isCancelledJourney = currentStatus === 'CANCELLED';
    const steps = [
        { status: 'CREATED', icon: 'wizardCard' },
        { status: 'IN_TRANSIT', icon: 'wizardTruck' },
        isCancelledJourney ? { status: 'CANCELLED' } : { status: 'DELIVERED', icon: 'wizardPersonWithBox' },
    ];
    return (_jsx("section", { className: styles.statusWizardSection, "data-testid": 'viewStatusWizardSection', children: steps.map((step, stepIndex) => {
            const isFinalCancelledStep = isCancelledJourney && stepIndex === currentStepIndex;
            const stepStatus = getWizardStepStatus(isFinalCancelledStep, currentStatus, stepIndex, currentStepIndex);
            const stepIcon = stepStatus === 'pending' ? `${step.icon}Inactive` : step.icon;
            const isStepBeforeCancelled = isCancelledJourney && stepIndex === currentStepIndex - 1;
            const isLastCompleted = currentStatus !== 'DELIVERED' &&
                !isCancelledJourney &&
                stepStatus === 'completed' &&
                stepIndex === currentStepIndex - 1;
            return (_jsxs("div", { className: `
              ${styles.wizardStep} ${styles[`step${stepStatus.charAt(0).toUpperCase() + stepStatus.slice(1)}`]} 
              ${isLastCompleted ? styles.lastCompleted : ''}
              ${isStepBeforeCancelled ? styles.lastBeforeCancelled : ''}
              ${stepIndex === 0 && stepStatus === 'current' ? styles.isCurrentFirstStep : ''}
            `, "data-testid": `viewStatusWizardStep${stepIndex}`, children: [_jsx("div", { className: styles.stepIcon, "data-testid": `viewStatusWizardStepIcon${stepIndex}`, children: stepIcon && _jsx(Icon, { name: stepIcon, "data-testid": `iconStatusWizardStepIcon${stepIndex}` }) }), _jsx("div", { className: styles.stepIndicator, "data-testid": `viewStatusWizardStepIndicator${stepIndex}`, children: renderStepIndicatorContent(stepStatus, stepIndex) }), _jsx("div", { className: styles.stepLabel, "data-testid": `textViewStatusWizardStepLabel${stepIndex}`, children: t(`cards.tracking.status.${step.status}`) }), stepIndex < steps.length - 1 && (_jsx("div", { className: styles.stepConnector, "data-testid": `viewStatusWizardStepConnector${stepIndex}` }))] }, step.status));
        }) }));
}
